// Price/spec formatting helpers
import type { PresetItem, PresetSpecs } from '../types/api.js';

export type SpecsSource = PresetSpecs | PresetItem | { specs?: PresetSpecs } | Record<string, unknown> | null | undefined;

export interface NormalizedPresetSpecs extends PresetSpecs {
  cpu: string;
  gpu: string;
  motherboard: string;
  ram: string;
  psu: string;
  storage: string;
  caseName: string;
  cooler: string;
}

export function buildPresetSpecs(source: SpecsSource): NormalizedPresetSpecs {
  var raw = source as PresetItem | PresetSpecs | { specs?: PresetSpecs } | null | undefined;
  var s: PresetSpecs = raw && typeof raw === 'object' && 'specs' in raw && raw.specs
    ? (raw.specs as PresetSpecs)
    : (raw as PresetSpecs) || {};
  return {
    cpu: String((s && s.cpu) || '').trim(),
    gpu: String((s && s.gpu) || '').trim(),
    motherboard: String((s && s.motherboard) || '').trim(),
    ram: String((s && s.ram) || '').trim(),
    psu: String((s && s.psu) || '').trim(),
    storage: String((s && s.storage) || '').trim(),
    caseName: String((s && s.caseName) || '').trim(),
    cooler: String((s && s.cooler) || '').trim()
  };
}

export function presetSpecsPairs(specsObj: SpecsSource): [string, string][] {
  var specs = buildPresetSpecs(specsObj);
  var raw = specsObj as PresetSpecs | { specs?: PresetSpecs } | null | undefined;
  var s: PresetSpecs =
    raw && typeof raw === 'object' && 'specs' in raw && raw.specs
      ? (raw.specs as PresetSpecs)
      : (raw as PresetSpecs) || {};
  var rows: [string, string][] = [
    ['Процессор', specs.cpu],
    ['Видеокарта', specs.gpu],
    ['Мат. плата', specs.motherboard],
    ['ОЗУ', specs.ram],
    ['Блок питания', specs.psu],
    ['Накопители', specs.storage],
    ['Охлаждение', specs.cooler],
    ['Корпус', specs.caseName]
  ];
  var buildColor = String((s && s.buildColor) || '').trim();
  if (buildColor) rows.push(['Цвет', buildColor]);
  return rows;
}

/** Целое число с пробелами между тысячами (115000 → 115 000) */
export function formatGroupedInt(n: number | string): string {
  var num = Math.round(Number(n));
  if (!isFinite(num)) return '';
  if (num === 0) return '0';
  var neg = num < 0;
  var s = String(Math.abs(num));
  var out = s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return neg ? '\u2212' + out : out;
}

export function formatPriceWithRub(price: string | number | null | undefined): string {
  var raw = String(price || '').trim();
  if (!raw) return '';
  var withPrefix = /^от\s+/i.test(raw);
  var tail = withPrefix ? raw.replace(/^от\s+/i, '').trim() : raw;
  tail = tail.replace(/[₽рp]+$/gi, '').replace(/\s+$/g, '').trim();
  var digitsOnly = tail.replace(/[^\d]/g, '');
  if (digitsOnly.length > 0) {
    var n = Number(digitsOnly);
    if (isFinite(n) && n >= 0) {
      return (withPrefix ? 'от ' : '') + formatGroupedInt(n) + ' ₽';
    }
  }
  var cleaned = raw.replace(/[₽рp]+$/gi, '').replace(/\s+$/g, '').trim();
  return cleaned ? cleaned + ' ₽' : '';
}

export function parsePriceNumber(price: string | number | null | undefined): number | null {
  // Поддержка форматов: "35 000", "35000", "35 000 ₽", "от 35 000 ₽"
  var s = String(price || '').trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/от\s+/g, '');
  // убираем всё, кроме цифр
  var digits = s.replace(/[^\d]/g, '');
  if (!digits) return null;
  var n = Number(digits);
  if (!isFinite(n) || n <= 0) return null;
  return n;
}

export function calcDiscountPercent(
  price: string | number | null | undefined,
  oldPrice: string | number | null | undefined
): number | null {
  var p = parsePriceNumber(price);
  var o = parsePriceNumber(oldPrice);
  if (!p || !o || o <= p) return null;
  var percent = Math.round(((o - p) / o) * 100);
  if (!isFinite(percent) || percent <= 0) return null;
  return percent;
}

export function formatRub(n: number | string): string {
  try {
    var g = formatGroupedInt(n);
    return g ? g + ' ₽' : String(n) + ' ₽';
  } catch (_) {
    return String(n) + ' ₽';
  }
}

export function escapeAttr(s: string | number | null | undefined): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function escapeHtmlText(s: string | number | null | undefined): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
